/**
 * Certificate (authenticated view) — celebration + download PNG/PDF + share.
 *
 * PNG: rendered directly on a 2D canvas (dependency-free, no SVG foreignObject
 *      → avoids Chromium's tainted-canvas SecurityError that we hit in
 *      iteration 17). Layout mirrors <CertificateView /> closely.
 * PDF: opens the browser print dialog with a print-scoped stylesheet so the
 *      user gets a clean, one-page PDF via "Save as PDF" (works cross-browser).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, Download, Copy, Share2, Printer, Linkedin, Check,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import CertificateView from "@/components/CertificateView";

function publicUrlFor(certId) {
    if (typeof window === "undefined") return `/api/og/certificat/${certId}`;
    // The URL surfaced to users (LinkedIn Share, Copy link) points at the
    // backend OG page: crawlers see the OG tags there, human visitors are
    // instantly redirected to the SPA route /certificat/{id}.
    return `${window.location.origin}/api/og/certificat/${certId}`;
}

const CERT_W = 1200;
const CERT_H = 840;
const COLORS = {
    paper: "#F5EFE6",
    ink: "#1E1E1E",
    inkSoft: "#5C574F",
    terracotta: "#C1552B",
    terracottaLight: "rgba(193, 85, 43, 0.15)",
    emerald: "#2D6A4F",
    border: "#D8CDBE",
};

function drawWrapped(ctx, text, x, y, maxWidth, lineHeight) {
    const words = String(text || "").split(" ");
    let line = "";
    let ny = y;
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            ctx.fillText(line, x, ny);
            line = w;
            ny += lineHeight;
        } else {
            line = test;
        }
    }
    if (line) ctx.fillText(line, x, ny);
    return ny;
}

function formatDateFr(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

function renderCertificate(canvas, cert) {
    const ctx = canvas.getContext("2d");
    canvas.width = CERT_W;
    canvas.height = CERT_H;

    // Background.
    ctx.fillStyle = COLORS.paper;
    ctx.fillRect(0, 0, CERT_W, CERT_H);

    // Terracotta ribbon on the left.
    ctx.fillStyle = COLORS.terracotta;
    ctx.fillRect(0, 0, 14, CERT_H);

    // Watermark top right.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "500 18px 'Helvetica Neue', Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`LEXIA SCHOOL · ${cert.language_iso || ""}`, CERT_W - 60, 60);

    // Left rounded rect + award-like circle glyph.
    ctx.textAlign = "left";
    ctx.fillStyle = COLORS.terracottaLight;
    // Rounded square 80x80 at 80, 80.
    const rx = 20;
    ctx.beginPath();
    ctx.moveTo(80 + rx, 90);
    ctx.arcTo(80 + 80, 90, 80 + 80, 90 + 80, rx);
    ctx.arcTo(80 + 80, 90 + 80, 80, 90 + 80, rx);
    ctx.arcTo(80, 90 + 80, 80, 90, rx);
    ctx.arcTo(80, 90, 80 + 80, 90, rx);
    ctx.closePath();
    ctx.fill();
    // Award ring
    ctx.strokeStyle = COLORS.terracotta;
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(120, 130, 22, 0, Math.PI * 2);
    ctx.stroke();
    // Award ribbons
    ctx.beginPath();
    ctx.moveTo(108, 150);
    ctx.lineTo(102, 172);
    ctx.lineTo(120, 162);
    ctx.lineTo(138, 172);
    ctx.lineTo(132, 150);
    ctx.stroke();

    // Eyebrow + title.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "500 20px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillText("CERTIFICAT DE COMPLÉTION", 190, 120);

    ctx.fillStyle = COLORS.ink;
    ctx.font = "600 30px 'Times New Roman', Times, serif";
    ctx.fillText(`Parcours professionnel · ${cert.track_title_fr || ""}`, 190, 158);

    // Body.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "500 22px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillText("Ce certificat atteste que", 80, 290);

    ctx.fillStyle = COLORS.ink;
    ctx.font = "700 92px 'Times New Roman', Times, serif";
    ctx.fillText(cert.first_name || "Apprenant", 80, 380);

    ctx.fillStyle = COLORS.ink;
    ctx.font = "500 26px 'Helvetica Neue', Arial, sans-serif";
    const langAlreadyHasVariant = /\(/.test(cert.language_fr || "");
    const variantLabel = cert.variant && !langAlreadyHasVariant ? ` (${cert.variant})` : "";
    const bodyText = (
        `a terminé l'intégralité des ${cert.modules_total} modules du parcours ${cert.track_title_fr} ` +
        `en ${cert.language_fr}${variantLabel} au niveau CECRL estimé ${cert.level}.`
    );
    drawWrapped(ctx, bodyText, 80, 430, CERT_W - 160, 34);

    // Divider.
    ctx.strokeStyle = COLORS.border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(80, 580);
    ctx.lineTo(CERT_W - 80, 580);
    ctx.stroke();

    // Bottom columns: date / modules / id.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "500 15px 'Helvetica Neue', Arial, sans-serif";
    ctx.fillText("DÉLIVRÉ LE", 80, 620);
    ctx.fillText("MODULES VALIDÉS", 470, 620);
    ctx.fillText("IDENTIFIANT", 860, 620);

    ctx.fillStyle = COLORS.ink;
    ctx.font = "600 26px 'Times New Roman', Times, serif";
    ctx.fillText(formatDateFr(cert.issued_at), 80, 660);
    ctx.fillText(`${cert.modules_completed} / ${cert.modules_total}`, 470, 660);
    ctx.font = "400 15px 'Courier New', monospace";
    ctx.fillText((cert.cert_id || "").slice(0, 26), 860, 660);
    if ((cert.cert_id || "").length > 26) {
        ctx.fillText((cert.cert_id || "").slice(26), 860, 682);
    }

    // Disclaimer.
    ctx.fillStyle = COLORS.inkSoft;
    ctx.font = "italic 500 15px 'Times New Roman', Times, serif";
    drawWrapped(
        ctx,
        cert.disclaimer_fr || "",
        80,
        750,
        CERT_W - 160,
        22,
    );
}

async function certificateToPngDataUrl(cert) {
    const canvas = document.createElement("canvas");
    renderCertificate(canvas, cert);
    return canvas.toDataURL("image/png");
}

export default function CertificatePage() {
    const { certId } = useParams();
    const navigate = useNavigate();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // 'png' | 'pdf' | 'copy' | null
    const [copied, setCopied] = useState(false);
    const cardRef = useRef(null);
    const [showCelebration, setShowCelebration] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/certificates/${certId}`);
                setCert(data.certificate);
                // Celebration on first arrival — cleared after ~2s.
                setShowCelebration(true);
                setTimeout(() => setShowCelebration(false), 2400);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, [certId]);

    const publicUrl = useMemo(() => publicUrlFor(certId), [certId]);
    const linkedinUrl = useMemo(() => {
        const u = encodeURIComponent(publicUrl);
        return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    }, [publicUrl]);

    async function handleDownloadPng() {
        setBusy("png");
        try {
            const dataUrl = await certificateToPngDataUrl(cert);
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `lexia-school-certificat-${cert.first_name.toLowerCase()}-${cert.track_id}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            toast.success("Certificat PNG téléchargé.");
        } catch (err) {
            toast.error("Impossible de générer le PNG. Utilisez « Imprimer » pour un PDF.");
        } finally {
            setBusy(null);
        }
    }

    function handlePrintPdf() {
        setBusy("pdf");
        try {
            window.print();
        } finally {
            setTimeout(() => setBusy(null), 400);
        }
    }

    async function handleCopyLink() {
        setBusy("copy");
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            toast.success("Lien de vérification copié.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Impossible de copier le lien.");
        } finally {
            setBusy(null);
        }
    }

    async function handleWebShare() {
        if (!navigator.share) {
            toast.info("Partage direct non disponible — copiez le lien.");
            return;
        }
        try {
            await navigator.share({
                title: `Certificat Lexia School — ${cert.track_title_fr}`,
                text: `${cert.first_name} a terminé le parcours ${cert.track_title_fr} en ${cert.language_fr}.`,
                url: publicUrl,
            });
        } catch (err) {
            // Web Share API rejects with `AbortError` when the user cancels
            // the share sheet — that's not an error. Log anything else.
            if (err?.name !== "AbortError") {
                console.warn("[Certificate] web share failed:", err);
            }
        }
    }

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-terracotta animate-spin" />
            </div>
        );
    }
    if (!cert) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center" data-testid="certificate-not-found">
                <p className="font-serif text-2xl">Certificat introuvable.</p>
                <button
                    onClick={() => navigate("/parcours")}
                    className="mt-4 text-sm text-terracotta hover:underline"
                >
                    Retour au parcours
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 print:p-0 print:max-w-none" data-testid="certificate-page">
            {/* Toolbar — hidden on print */}
            <div className="flex items-center justify-between mb-6 print:hidden">
                <button
                    onClick={() => navigate(`/parcours/${cert.track_id}`)}
                    className="text-inkSoft hover:text-ink text-sm inline-flex items-center gap-1"
                    data-testid="certificate-back"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour au parcours
                </button>
                {showCelebration && (
                    <div
                        className="text-[11px] tracking-[0.28em] uppercase text-emerald font-semibold animate-pulse"
                        data-testid="certificate-celebration"
                    >
                        Bravo ! Vous avez terminé le parcours.
                    </div>
                )}
            </div>

            <p className="text-xs tracking-[0.28em] uppercase text-inkSoft mb-3 print:hidden">
                Certificat de complétion
            </p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight print:hidden">
                Une reconnaissance de votre régularité.
            </h1>

            <div className="mt-6" data-testid="certificate-wrapper">
                <CertificateView ref={cardRef} certificate={cert} />
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-2 print:hidden">
                <button
                    onClick={handleDownloadPng}
                    disabled={busy === "png"}
                    data-testid="certificate-download-png"
                    className="inline-flex items-center gap-2 bg-ink text-white hover:bg-emerald rounded-full px-4 py-2 text-sm disabled:opacity-60 transition-colors"
                >
                    {busy === "png" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Télécharger en PNG
                </button>
                <button
                    onClick={handlePrintPdf}
                    disabled={busy === "pdf"}
                    data-testid="certificate-download-pdf"
                    className="inline-flex items-center gap-2 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-4 py-2 text-sm transition-colors"
                >
                    <Printer className="w-4 h-4" />
                    Imprimer / Enregistrer en PDF
                </button>
                <button
                    onClick={handleCopyLink}
                    data-testid="certificate-copy-link"
                    className="inline-flex items-center gap-2 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-4 py-2 text-sm transition-colors"
                >
                    {copied ? <Check className="w-4 h-4 text-emerald" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Lien copié" : "Copier le lien de vérification"}
                </button>
                <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="certificate-linkedin"
                    className="inline-flex items-center gap-2 bg-[#0A66C2] text-white hover:bg-[#004182] rounded-full px-4 py-2 text-sm transition-colors"
                >
                    <Linkedin className="w-4 h-4" />
                    Partager sur LinkedIn
                </a>
                <button
                    onClick={handleWebShare}
                    data-testid="certificate-webshare"
                    className="inline-flex items-center gap-2 bg-terracotta text-white hover:bg-terracottaDark rounded-full px-4 py-2 text-sm transition-colors"
                >
                    <Share2 className="w-4 h-4" />
                    Partager…
                </button>
            </div>

            <p className="mt-4 text-[11px] text-inkSoft italic print:hidden" data-testid="certificate-public-url">
                Lien public : <span className="font-mono text-ink">{publicUrl}</span>
            </p>
        </div>
    );
}
